import React from 'react';
import { createEvent } from '../../../api/events/client';

interface Result {
  noteMode: boolean;
  setNoteMode: React.Dispatch<React.SetStateAction<boolean>>;
  noteText: string;
  setNoteText: React.Dispatch<React.SetStateAction<string>>;
  noteSubmitting: boolean;
  enableNoteMode: () => void;
  handleNoteSubmit: () => Promise<void>;
  cancelNote: () => void;
}

// Owns the activity-note composer state machine: enable → textarea → submit
// (creates an `activity_received` event). The textarea visibility is driven
// by `noteMode`; the surrounding render branches off this flag.
export function useNoteMode(dossierId: string): Result {
  const [noteMode, setNoteMode] = React.useState(false);
  const [noteText, setNoteText] = React.useState('');
  const [noteSubmitting, setNoteSubmitting] = React.useState(false);

  const enableNoteMode = React.useCallback(() => {
    setNoteMode(true);
  }, []);

  const cancelNote = React.useCallback(() => {
    setNoteMode(false);
    setNoteText('');
  }, []);

  const handleNoteSubmit = React.useCallback(async () => {
    if (!dossierId || !noteText.trim() || noteSubmitting) {
      return;
    }
    setNoteSubmitting(true);
    try {
      await createEvent(dossierId, {
        event_type: 'activity_received',
        event_date: new Date().toISOString(),
        metadata: { description: noteText.trim() },
      });
      setNoteMode(false);
      setNoteText('');
    } finally {
      setNoteSubmitting(false);
    }
  }, [dossierId, noteText, noteSubmitting]);

  return {
    noteMode,
    setNoteMode,
    noteText,
    setNoteText,
    noteSubmitting,
    enableNoteMode,
    handleNoteSubmit,
    cancelNote,
  };
}
